import { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { ConfirmModal } from 'components/Shared/ConfirmModal';
import { useHistory } from 'hooks/generic/useHistory';
import { useToken } from 'hooks/user/useToken';
import { useSearchParams } from 'hooks/useSearchParams';
import { KYCDrawer } from '../components/KYCDrawer';
import { verifyEmail } from 'appRedux/restApi';
import { addQuery } from 'utils/generic/query-route';
import { EAccountRouteKeys } from 'constants/account/account-route-keys';
import User from 'assets/images/avatars/user.png';
import NoData from 'assets/images/generic/no-data.png';

const CONFIRM_MODAL_DATA = {
  success: {
    title: 'account.verifiedEmailNotificationSuccessTitle',
    description: 'account.verifiedEmailNotificationSuccessDescription',
    image: User
  },
  error: {
    title: 'account.verifiedEmailNotificationFailTitle',
    description: 'account.verifiedEmailNotificationFailDescription',
    image: NoData
  }
};

export const VerifyEmail: FC = () => {
  const { t } = useTranslation();
  const { goBack, history } = useHistory();
  const { token: queryToken } = useSearchParams();
  const [token, setToken] = useState(queryToken);
  const { setUserToken } = useToken();
  const { data, error } = useQuery({
    queryKey: [EAccountRouteKeys.ACCOUNT_VERIFY_EMAIL, token],
    queryFn: () => verifyEmail(token as string),
    retry: false,
    refetchOnWindowFocus: false
  });
  const [confirmModalVisible, setConfirmModalVisible] = useState(false);
  const [showKYC, setShowKYC] = useState(false);

  const info = error ? CONFIRM_MODAL_DATA.error : CONFIRM_MODAL_DATA.success;

  const handleCloseConfirmModal = () => {
    setConfirmModalVisible(false);
    goBack();
  };

  const closePage = () => {
    setShowKYC(false);
    setUserToken('');
    history.push('/');
  };

  useEffect(() => {
    if (!data && !error) return;
    if (data) {
      setShowKYC(true);
      setUserToken(data.data.token);
    }
    if (error) {
      setConfirmModalVisible(true);
      setShowKYC(false);
    }
  }, [data, error]);

  useEffect(() => {
    if (queryToken) {
      history.push(addQuery({ token: undefined }));
      setToken(queryToken);
    }
  }, [queryToken]);

  if (!token) return null;

  if (!data && !error) return null;

  return (
    <>
      {showKYC && <KYCDrawer closePage={closePage} />}
      <ConfirmModal
        image={info.image}
        title={t(info.title)}
        description={t(info.description)}
        confirmText={t('generic.ok')}
        visible={confirmModalVisible}
        onCancel={handleCloseConfirmModal}
        submitAsCancel
      />
    </>
  );
};
